import type { OfferingCardData } from 'components/HomepageV3/data/demoData';

/** A live industry filter row derived from the offering pool + taxonomy. */
export interface LiveIndustry {
  label: string;
  key: string;
  count: number;
}

const norm = (value: string): string => value.trim().toLowerCase();

const dedupeByCo = (cards: OfferingCardData[]): OfferingCardData[] => {
  const seen = new Set<string>();
  const out: OfferingCardData[] = [];
  cards.forEach((card) => {
    if (!card.co || seen.has(card.co)) return;
    seen.add(card.co);
    out.push(card);
  });
  return out;
};

/**
 * Build the Explore "Industries" filter list FE-only. The tombstone `categories`
 * field is a grab-bag (real industries mixed with marketing tags, company names
 * and test rows), so we intersect it with the canonical industry taxonomy
 * (`/primary_facade/v2/offerings/industries`) — only categories that match a real
 * industry survive. Counts are the number of distinct offerings in each industry
 * across the live pool. Returned sorted alphabetically, zero-count industries
 * dropped (mirrors control's aggregation).
 */
export function liveIndustryItems(
  cards: OfferingCardData[],
  taxonomyNames: string[],
): LiveIndustry[] {
  const allow = new Map(taxonomyNames.map((name) => [norm(name), name.trim()]));
  const counts = new Map<string, { label: string; count: number }>();

  dedupeByCo(cards).forEach((card) => {
    const seenForCard = new Set<string>();
    (card.categories ?? []).forEach((category) => {
      const canonical = allow.get(norm(category));
      if (!canonical) return;
      const key = norm(canonical);
      if (seenForCard.has(key)) return;
      seenForCard.add(key);
      const entry = counts.get(key) ?? { label: canonical, count: 0 };
      entry.count += 1;
      counts.set(key, entry);
    });
  });

  return Array.from(counts.values())
    .map((entry) => ({ label: entry.label, key: entry.label, count: entry.count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Live offering cards (deduped) tagged with the given industry. */
export function matchIndustryCards(
  cards: OfferingCardData[],
  industryKey: string,
): OfferingCardData[] {
  const target = norm(industryKey);
  return dedupeByCo(cards).filter((card) =>
    (card.categories ?? []).some((category) => norm(category) === target),
  );
}
